import {
  deleteCookie,
  getCookie,
} from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import { COOKIE_KEYS } from '@porterin/booking-flow/src/order-review/view/stores/OrderReviewStore';
import { isMoengageObjPresent } from '@porterin/component-library/src/ts-utils/analytics/moengage/MoEngage';
import { setMoengageUser } from '@porterin/component-library/src/ts-utils/analytics/moengage/MoEngageHelper';
import { captureException } from '@porterin/component-library/src/ts-utils/analytics/sentry/Sentry';
import { Dispatch, SetStateAction } from 'react';

// Auth/order-history repos + the order-history VM are still ReScript (their
// data-layer trees are a separate migration) — bridged via `.mjs`. Paths mirror
// the compiled MainLayoutHelper.mjs exactly.
import {
  handleAPIRequest,
  resolveError,
} from '../../../../shared/core/helpers/ApiResponseHelper';
import { isMoengageEnabled } from '../../../../shared/core/helpers/EventsTrackerHelper';
import {
  requirementType,
  whatsappConsent,
} from '../../../login/domain/helpers/AuthUtils';
import { getAuthRepo } from '../../../login/domain/repo/AuthRepoManager.mjs';
import type { AuthUser } from '../../../login/view-model/contexts/auth-context/AuthContextV1';
import { getRepo as getOrderHistoryRepo } from '../../../order-history/domian/repo/OrderHistoryRepoManager.mjs';
import { createVm } from '../../../order-history/view-model/OrderHistoryVm.mjs';

const GA_COOKIE = '_ga';

// ReScript `Catalyst.Loadable.t` runtime shapes (see compiled MainLayoutHelper.mjs):
// Loading(x) = { TAG: 0, _0: x }, Live(x) = { TAG: 1, _0: x }, Error(x) = { TAG: 2, _0: x }.
// (Init is the plain int 0, produced by the initial useState in useMainLayout.)
const loadable = (
  tag: 0 | 1 | 2,
  value: unknown,
): { TAG: number; _0: unknown } => ({
  TAG: tag,
  _0: value,
});

// Fields read off the customer-profile API response (bridged repo returns `any`).
interface CustomerProfileResponse {
  mobile: string;
  first_name: string;
  last_name: string;
  customer_uuid: string;
  mail_info: { email: string };
}

export function getVisitorId(): string {
  return getCookie(COOKIE_KEYS.VISITOR_ID) ?? '';
}

export function getGa4ClientId(): string {
  const gaCookie = getCookie(GA_COOKIE) ?? '';
  if (gaCookie === '') {
    return '';
  }
  const parts = gaCookie.split('.');
  return parts.length >= 4 ? `${parts[2] ?? ''}.${parts[3] ?? ''}` : '';
}

/**
 * TS port of `MainLayoutHelper.res.fetchProfileV2`. Fetches the customer
 * profile, seeds the auth user + MoEngage user on success (then runs
 * `onSuccess`), and clears auth cookies + reports to Sentry on failure. The
 * setters are the React state dispatchers from useMainLayout; they are called
 * with plain values (equivalent to the compiled `_ => value` updaters since the
 * next value never depends on the previous one).
 */
export function fetchProfileV2(
  setUser: Dispatch<SetStateAction<AuthUser | undefined>>,
  user: AuthUser | undefined,
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>,
  setIsAuthenticatedLoadable: Dispatch<SetStateAction<unknown>>,
  onSuccess: () => void,
): void {
  const webTrackingId = getCookie(COOKIE_KEYS.WEB_TRACKING_D) ?? '';
  const authToken = getCookie(COOKIE_KEYS.AUTH_TOKEN) ?? '';
  const mobile = getCookie(COOKIE_KEYS.MOBILE) ?? '';
  const repo = getAuthRepo();
  setIsAuthenticatedLoadable(loadable(0, true));

  handleAPIRequest(
    () => repo.customerProfile(webTrackingId, authToken, mobile),
    (data: CustomerProfileResponse): void => {
      setUser({
        auth_token: authToken,
        mobile: data.mobile,
        name: `${data.first_name} ${data.last_name}`,
        requirement_type: requirementType(user),
        email: data.mail_info.email,
        whatsapp_consent: whatsappConsent(user),
      });
      if (isMoengageObjPresent() && isMoengageEnabled()) {
        setMoengageUser({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.mail_info.email,
          mobile: data.mobile,
          customer_uuid: data.customer_uuid,
          client_id: getGa4ClientId(),
          session_id: getVisitorId(),
        });
      }
      onSuccess();
      setIsAuthenticated(true);
      setIsAuthenticatedLoadable(loadable(1, true));
    },
    (error: unknown): void => {
      captureException(resolveError(error));
      setIsAuthenticated(false);
      deleteCookie(COOKIE_KEYS.WEB_TRACKING_D);
      deleteCookie(COOKIE_KEYS.AUTH_TOKEN);
      deleteCookie(COOKIE_KEYS.MOBILE);
      setIsAuthenticatedLoadable(loadable(1, false));
    },
  );
}

/**
 * TS port of `MainLayoutHelper.res.fetchOrderHistory`. Loads the order history
 * into the passed loadable state setter, mapping through `OrderHistoryVm.createVm`
 * on success and reporting to Sentry on failure.
 */
export function fetchOrderHistory(
  setOrderHistory: Dispatch<SetStateAction<unknown>>,
): void {
  const webTrackingId = getCookie(COOKIE_KEYS.WEB_TRACKING_D) ?? '';
  const authToken = getCookie(COOKIE_KEYS.AUTH_TOKEN) ?? '';
  const mobile = getCookie(COOKIE_KEYS.MOBILE) ?? '';
  const repo = getOrderHistoryRepo();
  setOrderHistory(loadable(0, undefined));

  handleAPIRequest(
    () => repo.orderHistory(webTrackingId, authToken, mobile),
    (data: unknown): void =>
      setOrderHistory(loadable(1, createVm(data, mobile))),
    (error: unknown): void => {
      captureException(resolveError(error));
      setOrderHistory(loadable(2, error));
    },
  );
}
